import { supabase } from './supabase';
import { looksLikePhoneFragment, phoneSearchKey } from './phone';
import type {
  BookingRow,
  BookingSlotRow,
  ExperiencePickRow,
  ExperienceRow,
} from '../types';

// Admin's "booking" mental model maps 1:1 onto the new schema's
// departure — one orderline = one QR = one row on the calendar /
// today's view / search results. So every query in this module reads
// from booking.departures and joins through to the parent booking +
// the experience-picks + their slots.
//
// PostgREST can embed same-schema relations (booking.bookings ↔
// booking.departures ↔ booking.departure_experiences ↔
// booking.booking_slots) but not cross-schema (booking → experiences,
// booking_slots → infra.rooms). So we still hydrate experiences + room
// names in a second pass and merge client-side.

const SELECT_TREE = `
  id,
  departure_number,
  lifecycle_status,
  payment_status,
  locale,
  notes,
  created_at,
  booking:bookings!inner (
    id,
    customer_id,
    guest_name,
    customers ( first_name, last_name, email, phone )
  ),
  departure_experiences (
    id,
    experience_id,
    party_size,
    price_cents,
    pick_index,
    booking_slots ( id, slot_start, party_size, room_id, slot_index )
  )
`;

// PostgREST nested filters require !inner to be on the join the filter
// applies to. Slot-bound filters (date range for today's view) target
// the deepest booking_slots embed. The `departure_experiences` level
// ALSO needs `!inner` — without it, departures whose picks have no
// matching slots in today's window still come through with
// `departure_experiences: []`, and the today screen renders empty 0p
// ghost rows for every unrelated parent booking.
const SELECT_TREE_INNER_SLOTS = SELECT_TREE
  .replace('departure_experiences (', 'departure_experiences!inner (')
  .replace('booking_slots (', 'booking_slots!inner (');

// Raw shape PostgREST hands back before client-side flattening.
interface RawSlot {
  id: string;
  slot_start: string;
  party_size: number;
  room_id: string;
  slot_index: number;
}
interface RawPick {
  id: string;
  experience_id: string;
  party_size: number;
  price_cents: number | null;
  pick_index: number;
  booking_slots: RawSlot[];
}
interface RawDeparture {
  id: string;
  departure_number: string;
  lifecycle_status: BookingRow['lifecycle_status'];
  payment_status: BookingRow['payment_status'];
  locale: string;
  notes: string | null;
  created_at: string;
  booking: {
    id: string;
    customer_id: string | null;
    guest_name: string | null;
    customers: BookingRow['customers'];
  };
  departure_experiences: RawPick[];
}

async function hydrate(rawRows: RawDeparture[]): Promise<BookingRow[]> {
  if (rawRows.length === 0) return [];

  // Collect every distinct room + experience id across the rows for the
  // cross-schema hydration pass.
  const roomIds = new Set<string>();
  const experienceIds = new Set<string>();
  for (const r of rawRows) {
    for (const pick of r.departure_experiences) {
      experienceIds.add(pick.experience_id);
      for (const s of pick.booking_slots) roomIds.add(s.room_id);
    }
  }

  const [roomsRes, expsRes] = await Promise.all([
    roomIds.size === 0
      ? Promise.resolve({ data: [] as { id: string; name: string }[], error: null })
      : supabase
          .schema('infra')
          .from('rooms')
          .select('id, name')
          .in('id', [...roomIds]),
    experienceIds.size === 0
      ? Promise.resolve({ data: [] as ExperienceRow[], error: null })
      : supabase
          .schema('experiences')
          .from('experiences')
          .select('id, name, booking_image_url')
          .in('id', [...experienceIds]),
  ]);

  if (roomsRes.error) throw roomsRes.error;
  if (expsRes.error) throw expsRes.error;

  const roomMap = new Map<string, { name: string }>();
  for (const r of roomsRes.data ?? []) roomMap.set(r.id, { name: r.name });

  const expMap = new Map<string, ExperienceRow>();
  for (const e of (expsRes.data ?? []) as ExperienceRow[]) expMap.set(e.id, e);

  return rawRows.map((r) => {
    // Picks ordered by pick_index so screens render them in the order
    // the customer added them in the widget.
    const picks: ExperiencePickRow[] = [...r.departure_experiences]
      .sort((a, b) => a.pick_index - b.pick_index)
      .map((pick) => ({
        id: pick.id,
        experience_id: pick.experience_id,
        party_size: pick.party_size,
        price_cents: pick.price_cents,
        pick_index: pick.pick_index,
        experiences: expMap.get(pick.experience_id) ?? null,
      }));

    // Flatten every pick's slots into the BookingRow.booking_slots
    // array, sorted by slot_start so split-slot bookings render in
    // chronological order regardless of pick order.
    const slots: BookingSlotRow[] = r.departure_experiences
      .flatMap((pick) =>
        pick.booking_slots.map((s) => ({
          id: s.id,
          slot_start: s.slot_start,
          party_size: s.party_size,
          room_id: s.room_id,
          slot_index: s.slot_index,
          rooms: roomMap.get(s.room_id) ?? null,
          departure_experience_id: pick.id,
        })),
      )
      .sort((a, b) => a.slot_start.localeCompare(b.slot_start));

    const partySize = picks.reduce((s, p) => s + p.party_size, 0);
    const totalCents = picks.reduce(
      (s, p) => s + (p.price_cents ?? 0),
      0,
    );

    return {
      id: r.id,
      booking_number: r.departure_number,
      lifecycle_status: r.lifecycle_status,
      payment_status: r.payment_status,
      party_size: partySize,
      locale: r.locale,
      notes: r.notes,
      total_cents: totalCents > 0 ? totalCents : null,
      created_at: r.created_at,
      parent_booking_id: r.booking.id,
      guest_name: r.booking.guest_name ?? null,
      customers: r.booking.customers,
      booking_slots: slots,
      experiences: picks[0]?.experiences ?? null,
      experience_picks: picks,
    };
  });
}

export interface SlotWindow {
  startUtc: string;
  endUtc: string;
}

export async function fetchBookingsForToday(
  window: SlotWindow,
  filter: { roomIds?: string[] | null } = {},
): Promise<BookingRow[]> {
  // Inner-slot filter narrows to departures with at least one slot in
  // today's window. The slot embed becomes !inner so the date range
  // can be applied without dropping the parent rows.
  let q = supabase
    .schema('booking')
    .from('departures')
    .select(SELECT_TREE_INNER_SLOTS)
    .gte('departure_experiences.booking_slots.slot_start', window.startUtc)
    .lt('departure_experiences.booking_slots.slot_start', window.endUtc)
    .in('lifecycle_status', ['confirmed', 'arrived', 'no_show'])
    .order('created_at', { ascending: true })
    .limit(500);

  if (filter.roomIds && filter.roomIds.length > 0) {
    q = q.in('departure_experiences.booking_slots.room_id', filter.roomIds);
  }

  const { data, error } = await q;
  if (error) throw error;
  return hydrate((data ?? []) as unknown as RawDeparture[]);
}

export async function searchBookings(term: string): Promise<BookingRow[]> {
  const trimmed = term.trim();
  if (trimmed.length < 2) return [];

  const like = `%${trimmed.replace(/[%_]/g, '\\$&')}%`;

  // Search by departure_number (the customer-facing NEO-XXXX identifier).
  const byNumberPromise = supabase
    .schema('booking')
    .from('departures')
    .select(SELECT_TREE)
    .ilike('departure_number', like)
    .order('created_at', { ascending: false })
    .limit(50);

  // Search by customer fields — needs an !inner join on bookings + customers
  // so PostgREST can apply the foreignTable filter.
  //
  // Phone: customers are stored as "+31 6 12345678" (spaces vary) while
  // staff type "06 12345678" / "0612345678" / "+316…". The raw ILIKE keeps
  // exact-format hits; the generated customers.phone_search column (digits,
  // NL country code + trunk 0 stripped) catches every other spelling once
  // the typed term is normalised the same way.
  const customerClauses = [
    `email.ilike.${like}`,
    `phone.ilike.${like}`,
    `first_name.ilike.${like}`,
    `last_name.ilike.${like}`,
  ];
  if (looksLikePhoneFragment(trimmed)) {
    customerClauses.push(`phone_search.ilike.%${phoneSearchKey(trimmed)}%`);
  }
  const byCustomerPromise = supabase
    .schema('booking')
    .from('departures')
    .select(SELECT_TREE.replace('customers (', 'customers!inner ('))
    .or(customerClauses.join(','), { foreignTable: 'booking.customers' })
    .order('created_at', { ascending: false })
    .limit(50);

  // Search by the name typed at booking time (bookings.guest_name). Covers
  // anonymous counter walk-ins that have no customer row at all, and
  // bookings where the typed name differs from the customer record.
  const byGuestNamePromise = supabase
    .schema('booking')
    .from('departures')
    .select(SELECT_TREE)
    .ilike('booking.guest_name', like)
    .order('created_at', { ascending: false })
    .limit(50);

  const [byNumber, byCustomer, byGuestName] = await Promise.all([
    byNumberPromise,
    byCustomerPromise,
    byGuestNamePromise,
  ]);
  if (byNumber.error) throw byNumber.error;
  if (byCustomer.error) throw byCustomer.error;
  if (byGuestName.error) throw byGuestName.error;

  const merged = new Map<string, RawDeparture>();
  for (const r of (byNumber.data ?? []) as unknown as RawDeparture[]) merged.set(r.id, r);
  for (const r of (byCustomer.data ?? []) as unknown as RawDeparture[]) merged.set(r.id, r);
  for (const r of (byGuestName.data ?? []) as unknown as RawDeparture[]) merged.set(r.id, r);

  // Each source is already newest-first; re-sort the union and cap it so
  // the merged list keeps the same ordering + limit as a single query.
  const rows = [...merged.values()]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, 50);

  return hydrate(rows);
}

// Returns every OTHER departure under the same parent booking — used by
// BookingDetailScreen to render an "Andere vertrekken in deze boeking"
// section so staff can jump between siblings of a multi-departure cart
// (one cart → multiple departure times, each with its own QR + email).
// Returns [] for single-departure bookings, which is the common case.
export async function fetchSiblingDepartures(
  parentBookingId: string,
  excludeDepartureId: string,
): Promise<BookingRow[]> {
  const { data, error } = await supabase
    .schema('booking')
    .from('departures')
    .select(SELECT_TREE)
    .eq('booking_id', parentBookingId)
    .neq('id', excludeDepartureId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return hydrate((data ?? []) as unknown as RawDeparture[]);
}

export async function fetchBookingByNumber(number: string): Promise<BookingRow | null> {
  const { data, error } = await supabase
    .schema('booking')
    .from('departures')
    .select(SELECT_TREE)
    .eq('departure_number', number)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  const [hydrated] = await hydrate([data as unknown as RawDeparture]);
  return hydrated ?? null;
}
